import React, { useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './BrandOnboardingImagePanel.scss';

import ImageUploader from '../../General/ImageUploader';
import DropUploader from '../../Uploader/DropUploader';
import BrandOnboardingProgressBar from '../BrandOnboardingProgressBar';

import { updateBrand } from '../../../Actions/BrandActions';
import { getBrand } from '../../../Helpers/user_helpers';

const KB_SIZE = 1024; // 1 KB
const MB_SIZE = 1024 * KB_SIZE; // 1 MB

const BrandOnboardingImagePanel = props => {
  const { user } = props;

  const [uploadType, setUploadType] = useState(''); // 'logo' | 'mobileCoverImage' | 'mobileBadgeImage'
  const updateField = (prev, idx, patch) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
  const [fields, setFields] = useState([
    {
      title: 'Logo',
      subtitle: 'Upload one image to use as your official brand logo.',
      disclaimer: 'Upload one image to use as your official brand logo. Must be PNG or JPEG with a transparent or white background.',
      value: getBrand(user)?.logo || '',
      brandField: 'logo',
      isUploading: false,
      uploadProgress: 0,
      maxSize: 2 * MB_SIZE,
      prettySize: '2 MB',
      acceptedFileTypes: ['image/png', 'image/jpeg'],
      expectedAspectRatio: null,
      prettyExpectedDimensions: ''
    },
    {
      title: 'Cover Image',
      subtitle: 'Share a lifestyle image that captures your brand’s look and feel.',
      disclaimer: 'Think cover photo with no words or logo. PNG or JPEG format, 1920 x 1080 px (standard Instagram size).',
      value: getBrand(user)?.mobileCoverImage || '',
      brandField: 'mobileCoverImage',
      isUploading: false,
      uploadProgress: 0,
      maxSize: 10 * MB_SIZE,
      prettySize: '10 MB',
      acceptedFileTypes: ['image/png', 'image/jpeg'],
      expectedAspectRatio: 16 / 9,
      prettyExpectedDimensions: '1920 x 1080'
    },
    {
      title: 'Badge Image',
      subtitle: 'Provide a different lifestyle image that captures your brand look and feel.',
      disclaimer: 'Think of a badge style photo with no words or logo. PNG or JPEG format, 600 x 600 px.',
      value: getBrand(user)?.mobileBadgeImage || '',
      brandField: 'mobileBadgeImage',
      isUploading: false,
      uploadProgress: 0,
      maxSize: 10 * MB_SIZE,
      prettySize: '10 MB',
      acceptedFileTypes: ['image/png', 'image/jpeg'],
      expectedAspectRatio: 1,
      prettyExpectedDimensions: '600 x 600'
    }
  ]);
  const canGoToNextPanel = fields.every(f => f.value && !f.isUploading);

  const onNextClick = () => {
    if (props.isLoading) return;
    else if (!canGoToNextPanel) return window.ALERT.error('Please upload images for all fields before proceeding.');

    props.goToNextPanel();
  };
  const onDrop = (acceptedFiles, idx) => {
    const firstFile = acceptedFiles?.[0];
    if (!firstFile) return;

    const field = fields[idx];
    if (firstFile.size > field.maxSize) {
      window.ALERT.error(`File size exceeds the limit of ${field.prettySize}.`);
      return;
    }

    setFields(prev => updateField(prev, idx, { isUploading: true }));
    return [firstFile]; // Return the accepted files to be processed by the uploader
  };
  const onProgress = (progress, idx) => setFields(prev => updateField(prev, idx, { uploadProgress: progress }));
  const onUpload = async (imageUrl, idx) => {
    if (!imageUrl) return;
    const field = fields[idx];

    if (field.expectedAspectRatio) {
      const image = new window.Image();
      image.src = imageUrl;
      await new Promise(resolve => {
        image.onload = resolve;
        image.onerror = () => {
          window.ALERT.error('Failed to load the uploaded image. Please try again.');
          setFields(prev => updateField(prev, idx, { isUploading: false }));
          resolve();
        };
      });

      const aspectRatio = image.naturalWidth / image.naturalHeight;
      // Allow a 10% margin of error in aspect ratio
      if (Math.abs(aspectRatio - field.expectedAspectRatio) > 0.1) {
        window.ALERT.error(`Image aspect ratio should be ${field.prettyExpectedDimensions}. Please upload an image with the correct dimensions.`);
        setFields(prev => updateField(prev, idx, { isUploading: false }));
        return;
      }
    }

    await props.updateBrand(getBrand(user), { [field.brandField]: imageUrl });
    setFields(prev => updateField(prev, idx, { value: imageUrl, isUploading: false }));
  };

  return (
    <div className='brand-onboarding-image-panel-outer'>
      <div className='brand-onboarding-image-panel-inner'>
        <ImageUploader
          isVisible={!!uploadType}
          setIsVisible={() => setUploadType('')}
          initialImageUrl={fields.find(f => f.brandField === uploadType)?.value || ''}
          onSaveCallback={imageUrl => {
            const idx = fields.findIndex(f => f.brandField === uploadType);
            if (idx !== -1) {
              setFields(prev => updateField(prev, idx, { value: imageUrl }));
              props.updateBrand(getBrand(user), { [uploadType]: imageUrl });
            }
            setUploadType('');
          }}
        />

        <div className='brand-onboarding-split-container-outer'>
          <div className='brand-onboarding-split-container-inner'>
            <div className='brand-onboarding-graphic-container-outer'>
              <div className='brand-onboarding-graphic-container-inner'>
                <div className='brand-onboarding-graphic-container-stat'>
                  <div className='brand-onboarding-graphic-container-stat-label'>Our Numbers</div>
                  <div className='brand-onboarding-graphic-container-stat-number'>{props.featuredStat.number}+</div>
                  <div className='brand-onboarding-graphic-container-stat-description'>{props.featuredStat.description}</div>
                </div>

                <BrandOnboardingProgressBar
                  goToPanel={props.goToPanel}
                  sections={props.sections}
                  activeSectionName={props.activeSectionName}
                  activePanelName={props.activePanelName}
                />
              </div>
              <div className='brand-onboarding-graphic-background'>
                <img className='brand-onboarding-graphic-background-image' src={props.backgroundImage} alt='name panel graphic' />
                <div className='brand-onboarding-graphic-background-overlay'></div>
              </div>
            </div>

            <div className='brand-onboarding-input-container-outer'>
              <div className='brand-onboarding-input-container-number-marker'>{props.panelNumber}</div>

              <div className={cn('brand-onboarding-input-container-inner', { isLoading: props.isLoading })}>
                <div className='brand-onboarding-title-text'>Brand Details</div>
                <div className='brand-onboarding-subtitle-text'>Please provide us with the following information and assets.</div>

                {fields.map((field, idx) => {
                  const { value, isUploading, uploadProgress, acceptedFileTypes } = field;

                  return (
                    <div className='brand-onboarding-image-field-container' key={idx}>
                      <div className='brand-onboarding-input-label'>{field.title}</div>
                      <div className='brand-onboarding-input-sublabel'>{field.subtitle}</div>

                      <DropUploader
                        allowClicks
                        onProgress={progress => onProgress(progress, idx)}
                        onUpload={imageUrl => onUpload(imageUrl, idx)}
                        onDropHandleAccepted={acceptedFiles => onDrop(acceptedFiles, idx)}
                        allowOnlyImages
                        customAcceptTypes={acceptedFileTypes}
                      >
                        <div className={cn('brand-onboarding-image-field-upload', { uploading: field.isUploading })}>
                          {value ? value : isUploading ? `Uploading ${uploadProgress}%` : 'click or drop file to upload'}
                        </div>
                      </DropUploader>

                      <div className='brand-onboarding-input-disclaimer'>{field.disclaimer}</div>
                    </div>
                  );
                })}

                <div className='brand-onboarding-pagination-buttons'>
                  <button className='brand-onboarding-button stretch' onClick={() => props.goToPreviousPanel()}>
                    Back
                  </button>
                  <button className={cn('brand-onboarding-button stretch primary', { disabled: !canGoToNextPanel })} onClick={onNextClick}>
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BrandOnboardingImagePanel.propTypes = {
  onboarding: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  updateBrand: PropTypes.func.isRequired,

  goToPreviousPanel: PropTypes.func.isRequired,
  goToNextPanel: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { onboarding, user } = state;
  return { onboarding, user };
};

export default connect(mapStateToProps, {
  updateBrand
})(withRouter(BrandOnboardingImagePanel));
